/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-negated-condition */
/**
 * @file 侧边导航任务列表
 * @author sunyingying01
 */

import React, {useState, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useLocation} from 'react-router-dom';
import cx from 'classnames';
import {Typography} from 'antd';

import {UserStore, JobStore, JobsListStore} from '@/stores';
import {IconMap} from '@/components/IconMap';
import RenameDeleteMenu from '@/components/RenameDeleteMenu';
import {RESTRICT_TIP} from '@/modules/MigrationModules/constants';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import styles from './index.module.less';

interface JobListProps {
  store: JobsListStore;
  userStore: UserStore;
  handleRenameJob: (job: JobStore) => void;
  handleCopyJob?: (job: JobStore) => void;
  handleDeleteJob: (job: JobStore) => void;
  handleSelectJob: (job: JobStore) => void;
}

const JobList: React.FC<JobListProps> = observer(props => {
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [renameDeleteOpen, setRenameDeleteOpen] = useState<boolean>(false);
    const location = useLocation();
    const isBillingPage = location.pathname === '/billing';

    const {store} = props;

    const selectedJobId = jobIdSyncService.getJobIdFromUrl();
    useEffect(
        () => {
            if (selectedJobId !== store.selectedJobId) {
                store.setSelectedJobId(selectedJobId);
            }
        },
        [selectedJobId]
    );

    return (
        <div className={styles.taskList}>
            {store.jobsList.length > 0 && store.jobsList.map((job: JobStore) => {
                const isActive = activeJobId === job.job_id;
                const isDisabled = ['COPYING'].includes(job.status as string);
                const isCopyDisabled = store.checkExceededlimit
                    || job.isSpecialPlatform
                    || props.userStore.isMigrationRestricted;

                return (
                    <div
                        className={cx(
                            styles.jobItem,
                            {[styles.itemActive]: !isBillingPage && job.job_id === store.selectedJobId},
                            {[styles.menuOpenJobItem]: isActive},
                            {[styles.taskHoverActionWrapper]: renameDeleteOpen && job.job_id === store.selectedJobId},
                            {[styles.itemDisabled]: isDisabled}
                        )}
                        key={`${job.job_id}_${job.name}`}
                        onClick={() => {
                            if (isDisabled) {
                                return;
                            }
                            props.handleSelectJob(job);
                        }}

                    >
                        <div className={styles.item}>
                            <div className={styles.statusIcon}>
                                {IconMap[job.status || 'DRAFT']}
                            </div>
                            <div className={styles.itemTitle}>
                                <Typography.Paragraph
                                    ellipsis={{
                                        rows: 1,
                                        tooltip: {
                                            title: job.name,
                                            styles: {root: {maxWidth: 'fit-content'}}
                                        }
                                    }}
                                >
                                    {job.name}
                                </Typography.Paragraph>
                            </div>
                        </div>
                        <div className={cx(styles.taskActionWrapper,
                            {[styles.taskActionActive]: isActive},
                            {[styles.taskHoverActionWrapper]: renameDeleteOpen && job.job_id === activeJobId},
                            // eslint-disable-next-line max-len
                            {[styles.taskActiveHoverActionWrapper]: renameDeleteOpen && job.job_id === store.selectedJobId}
                        )}
                        >
                            <RenameDeleteMenu
                                disabled={isDisabled}
                                onRename={() => props.handleRenameJob(job)}
                                onCopy={props.handleCopyJob ? () => props.handleCopyJob?.(job) : undefined}
                                showCopy={!!props.handleCopyJob}
                                disableCopy={isCopyDisabled}
                                disableRename={props.userStore.isMigrationRestricted}
                                disableDelete={props.userStore.isMigrationRestricted}
                                copyDisabledTooltip={(() => {
                                    if (props.userStore.isMigrationRestricted) {
                                        return RESTRICT_TIP;
                                    }
                                    if (job.isSpecialPlatform) {
                                        return '';
                                    }
                                    const planText = props.userStore.subscriptionPlanText;
                                    const limit = JobsListStore.LimitNumber;
                                    const isPro = props.userStore.subscriptionPlan?.toLowerCase() === 'pro';
                                    return `${planText}账号仅支持创建${limit}个任务，请升级${isPro ? '企业版' : '进阶版'}账号。`;
                                })()}
                                onDelete={() => props.handleDeleteJob(job)}
                                onOpenChange={open => {
                                    setRenameDeleteOpen(open);
                                    setActiveJobId(open ? job.job_id : null);
                                }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default JobList;
